import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { DropdownCaret } from "components/BudgetBuilder/Overview/OverviewLineItemsList/DropdownCaret";
import OverviewInternalCostShareMemberRow
	from "components/BudgetBuilder/Overview/OverviewLineItemsList/OverviewInternalCostShareMemberRow";
import OverviewInternalCostShareRow
	from "components/BudgetBuilder/Overview/OverviewLineItemsList/OverviewInternalCostShareRow";
import { useIsExpandedContext } from "components/BudgetBuilder/Overview/OverviewLineItemsList/OverviewLineItemsList";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Table from "components/Table";
import { budgetBuilderSelectors, tenantSelectors } from "store/selectors";
import {
	convertCategoriesToOptions,
	convertOptionsToTextByValue,
	getCostShareSubtotal,
	getPeriodSubtotal,
	getResearchTeamMemberDataByPeriodByMember,
} from "utils/budgetBuilder";

import styles from "./styles.css";

const getInternalCostShareData = (internalCostShareByPeriod, numberOfPeriods) => {
	let result = {};
	if (internalCostShareByPeriod) {
		for (let periodNumber in internalCostShareByPeriod) {
			const internalCostShareData = internalCostShareByPeriod[periodNumber];
			if (isNonEmptyArray(internalCostShareData)) {
				for (let costShare of internalCostShareData) {
					let key = costShare.value;
					let costShareData = result[key] || getDefaultCostShareDataList(numberOfPeriods, costShare.value);
					costShareData[periodNumber - 1] = costShare;
					result[key] = costShareData;
				}
			}
		}
	}

	return result;
};

const getDefaultCostShareDataList = (numberOfPeriods, value) => Array.from(Array(numberOfPeriods).keys())
	.map(() => ({ value }));


export const OverviewInternalCostShare = () => {
	const { currencyFormatter } = useTenantFormatter();
	const { isExpandedState: { isInternalCostShareExpanded }, isExpandedDispatch } = useIsExpandedContext();
	const setIsInternalCostShareExpanded = useCallback(
		isExpanded => isExpandedDispatch({ type: "isInternalCostShareExpanded", isExpanded }),
		[ isExpandedDispatch ],
	);

	useEffect(() => {
		isExpandedDispatch({ type: "isInternalCostShareExpanded", isExpanded: true });
	}, [ isExpandedDispatch ]);

	const categories = useSelector(tenantSelectors.proposalCategories);
	const periodList = useSelector(budgetBuilderSelectors.periodRows);
	const internalCostShareByPeriod = useSelector(budgetBuilderSelectors.internalCostShareByPeriod);
	const researchTeamByPeriod = useSelector(budgetBuilderSelectors.researchTeamByPeriod);
	const internalCostShareData = useMemo(
		() => getInternalCostShareData(internalCostShareByPeriod, periodList.length),
		[ internalCostShareByPeriod, periodList.length ]
	);
	const researchTeamData = useMemo(
		() => getResearchTeamMemberDataByPeriodByMember(researchTeamByPeriod, periodList.length),
		[ periodList.length, researchTeamByPeriod ]
	);

	const categoryTextByValue = useMemo(
		() => convertOptionsToTextByValue(convertCategoriesToOptions(categories)),
		[ categories ]
	);
	const totalsByPeriod = useMemo(
		() => {
			const totalByPeriod = {};
			for (let period of periodList) {
				const periodNumber = period.periodNumber;
				const lineItemsTotal = getCostShareSubtotal(internalCostShareByPeriod?.[periodNumber]);
				const researchTeamTotal = getPeriodSubtotal(researchTeamByPeriod?.[periodNumber], true);
				totalByPeriod[periodNumber] = lineItemsTotal + researchTeamTotal;
			}

			return totalByPeriod;
		},
		[ internalCostShareByPeriod, periodList, researchTeamByPeriod ]
	);

	const total = useMemo(
		() => Object.values(totalsByPeriod).reduce((runningTotal, total) => runningTotal + total, 0),
		[ totalsByPeriod ]
	);

	const toggleExpanded = () => setIsInternalCostShareExpanded(!isInternalCostShareExpanded);

	return (
		<>
			<Table.Row>
				<Table.Cell className={`${styles.cellAlignLeft} ${styles.expandCell}`}>
					<span>{"Internal Cost Share"}</span>
					<DropdownCaret
						isExpanded={isInternalCostShareExpanded}
						onClick={toggleExpanded}
					/>
				</Table.Cell>
				{isInternalCostShareExpanded && periodList?.map((period, p) => (
					<Table.Cell key={`period-${p}`}>
						<div className={styles.cellAlignRight}>
							<span className={styles.bold}>{"Amount"}</span>
						</div>
					</Table.Cell>
				))}
				{!isInternalCostShareExpanded && Object.entries(totalsByPeriod).map(([ p, total ]) => (
					<Table.Cell className={styles.cellAlignRight} key={`summary-${p}`}>
						<span className={styles.bold}>{currencyFormatter(total)}</span>
					</Table.Cell>
				))}
				<Table.Cell className={styles.cellAlignRight}>
					{!isInternalCostShareExpanded && (
						<span className={styles.bold}>
							{currencyFormatter(total)}
						</span>
					)}
				</Table.Cell>
			</Table.Row>
			{isInternalCostShareExpanded && Object.entries(internalCostShareData).map(([ i, items ]) => (
				<OverviewInternalCostShareRow
					key={`internal-cost-share-item-${i}`}
					categoryTextByValue={categoryTextByValue}
					items={items}
				/>
			))}
			{isInternalCostShareExpanded && Object.entries(researchTeamData).map(([ i, items ]) => (
				<OverviewInternalCostShareMemberRow
					key={`internal-cost-share-member-${i}`}
					items={items}
					index={i}
				/>
			))}
			{isInternalCostShareExpanded && (
				<Table.Row>
					<Table.Cell className={styles.cellAlignLeft}>
						<span className={styles.bold}>{"Internal Cost Share Total"}</span>
					</Table.Cell>
					{Object.entries(totalsByPeriod).map(([ p, total ]) => (
						<Table.Cell className={styles.cellAlignRight} key={`summary-${p}`}>
							<span className={styles.bold}>{currencyFormatter(total)}</span>
						</Table.Cell>
					))}
					<Table.Cell className={styles.cellAlignRight}>
						<span className={styles.bold}>{currencyFormatter(total)}</span>
					</Table.Cell>
				</Table.Row>
			)}
		</>
	);
};
